import type { SeriesActionsTemplate } from '../../../../core/types/series-actions-template.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type {
  AreaSeriesToMetadata,
  AreaSeriesWithDatapoints,
  MapDatapoints,
} from '../../../types/xy-chart-internals.js';
import { buildAreaSeriesActionItem } from '../../../utils/build-series-action-item.js';
import {
  isAreaTooltipContext,
  type ClosestDatapoint,
  type ClosestSeries,
  type ClosestSeriesToMetadata,
  type TooltipContextClosestPoints,
} from '../../../utils/type-guards.js';

export interface BuildClosestPointTooltipHeaderPropsArgs<
  T extends ClosestSeries,
  K extends ClosestDatapoint<T>,
> {
  datapoint: K;
  series: T;
  seriesToMetadata: ClosestSeriesToMetadata<T>;
  seriesWithDatapoints: AreaSeriesWithDatapoints[];
  datapointMetadataMap: MapDatapoints;
}

export const buildClosestPointTooltipHeaderProps = <
  T extends ClosestSeries,
  K extends ClosestDatapoint<T>,
>({
  datapoint,
  series,
  seriesToMetadata,
  seriesWithDatapoints,
  datapointMetadataMap,
}: BuildClosestPointTooltipHeaderPropsArgs<T, K>) => {
  const metadata = safeGet(seriesToMetadata, series);

  const enrichedDatapoint: TooltipContextClosestPoints = {
    series,
    datapoint,
    metadata,
  };

  const { name = '', color = { Default: 'blue' } } = metadata;

  const shape = 'shape' in metadata ? metadata.shape : undefined;

  let actions: (() => SeriesActionsTemplate) | undefined = undefined;

  if (isAreaTooltipContext(enrichedDatapoint)) {
    const { actions: seriesActions } = enrichedDatapoint.metadata;
    actions = seriesActions
      ? () => {
          const actionItem = buildAreaSeriesActionItem({
            datapoint: enrichedDatapoint.datapoint,
            allSeries: seriesWithDatapoints,
            seriesToMetadata:
              seriesToMetadata as unknown as AreaSeriesToMetadata,
            datapointMetadataMap,
            currentSeries: enrichedDatapoint.series,
          });
          return seriesActions(actionItem);
        }
      : undefined;
  } else {
    const { actions: seriesActions } = enrichedDatapoint.metadata;
    actions = () =>
      (
        seriesActions as (
          datapoint: K & { name: string },
        ) => SeriesActionsTemplate
      )?.({
        ...datapoint,
        name,
      });
  }

  return {
    color: color.Default,
    name,
    actions,
    shape,
  };
};
